// Modal showing detailed breakdown of user's obligations (installments + borrows).
import { useEffect, useState } from "react";
import { X, Car, Coins, Receipt } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useCommitments } from "@/lib/useCommitmentsCache";
import { useCheques } from "@/lib/useCheques";
import { useAccounts } from "@/lib/useAccounts";
import { CommitmentsSection } from "@/components/dashboard/CommitmentsSection";
import { BorrowsSection } from "@/components/dashboard/BorrowsSection";
import { IncomingChequesSection } from "@/components/dashboard/IncomingChequesSection";
import { StatusPill, installmentStatus } from "@/components/dashboard/StatusPill";

export function ObligationsDetails({ open, onClose, installments, ccy, borrowTotal = 0 }) {
  const { t, lang } = useI18n();
  const [borrows, setBorrows] = useState([]);
  const commits = useCommitments();
  const { items: chqs = [] } = useCheques();
  const { items: accounts = [] } = useAccounts();
  const creditCards = accounts.filter((a) => a.card_kind === "credit" && Number(a.balance ?? a.opening_balance ?? 0) < 0);

  useEffect(() => {
    if (!open) return;
    api.get("/transactions?type=borrow&limit=500").then((r) => setBorrows(r.data)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const installTotal = installments.reduce((s, i) => s + Math.max(0, i.total_installments - (i.paid_count || 0)) * (i.amount || 0), 0);
  const ccDebt = creditCards.reduce((s, a) => s + Math.max(-Number(a.balance ?? a.opening_balance ?? 0), 0), 0);
  const borrowSum = borrowTotal || (borrows.reduce((s, b) => s + (b.final_amount || b.amount || 0), 0) + ccDebt);
  const commitMonthly = commits.total_monthly || 0;
  const chqPending = chqs.filter((c) => (c.status || "pending") === "pending" && (c.direction || "outgoing") === "outgoing");
  const chqIncoming = chqs.filter((c) => (c.status || "pending") === "pending" && c.direction === "incoming");
  const chqTotal = chqPending.reduce((s, c) => s + Number(c.amount || 0), 0);
  const chqIncomingTotal = chqIncoming.reduce((s, c) => s + Number(c.amount || 0), 0);

  return (
    <div data-testid="obligations-modal" className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm grid place-items-end sm:place-items-center p-3" onClick={onClose}>
      <div className="w-full sm:max-w-md neu p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-extrabold neu-accent-blue">{t("obligationsDetails")}</h3>
          <button onClick={onClose} data-testid="obl-details-close" className="w-9 h-9 rounded-full neu-sm grid place-items-center active:scale-95">
            <X className="w-4 h-4" />
          </button>
        </div>

        <CommitmentsSection items={commits.items || []} ccy={ccy} />

        <section className="space-y-2 mb-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2"><Car className="w-3.5 h-3.5" /> {t("installments")}</p>
          {installments.length === 0 && <p className="text-xs text-slate-400 py-2">—</p>}
          {installments.map((i) => {
            const rem = Math.max(0, i.total_installments - (i.paid_count || 0));
            const remAmount = rem * (i.amount || 0);
            return (
              <div key={i.id} className="p-2.5 rounded-xl neu-sm flex items-center gap-2" data-testid={`obl-inst-row-${i.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{i.name}</p>
                  <p className="text-xs text-slate-500">{rem} × {(i.amount || 0).toFixed(0)} {ccy}</p>
                </div>
                <StatusPill status={installmentStatus(i)} lang={lang} testId={`obl-inst-status-${i.id}`} />
                <span className="font-display font-extrabold text-sm tabular-nums text-warn">{remAmount.toFixed(0)}</span>
              </div>
            );
          })}
        </section>

        <BorrowsSection borrows={borrows} creditCards={creditCards} ccy={ccy} />

        <section className="space-y-2 mb-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2"><Receipt className="w-3.5 h-3.5" /> {lang === "ar" ? "شيكات غير مدفوعة" : "Unpaid cheques"}</p>
          {chqPending.length === 0 && <p className="text-xs text-slate-400 py-2">—</p>}
          {chqPending.map((c) => (
            <div key={c.id} className="p-2.5 rounded-xl neu-sm flex items-center gap-2" data-testid={`obl-chq-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{c.payee}</p>
                <p className="text-xs text-slate-500">{c.due_date} · {c.bank_name || "—"}</p>
              </div>
              <StatusPill status="unpaid" lang={lang} testId={`obl-chq-status-${c.id}`} />
              <span className="font-display font-extrabold text-sm tabular-nums text-violet-700">{Number(c.amount || 0).toFixed(0)}</span>
            </div>
          ))}
        </section>

        {chqIncoming.length > 0 && (
          <IncomingChequesSection items={chqIncoming} ccy={ccy} />
        )}

        <div className="p-3 rounded-2xl neu-inset flex items-center justify-between">
          <span className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Coins className="w-3.5 h-3.5" /> {t("totalObligations")}</span>
          <span className="font-display text-xl font-extrabold neu-accent-blue tabular-nums">{(installTotal + borrowSum + commitMonthly + chqTotal).toFixed(0)} {ccy}</span>
        </div>
      </div>
    </div>
  );
}
